// client/src/writer/utils/chapterContentCache.ts
// Enhanced localStorage-first chapter content caching utility

interface CachedChapterContent {
  content: string;
  versionHistory: Array<{
    content: string;
    generatedSummary: string;
    timestamp: number;
  }>;
  lastModified: number;
  status?: string;
}

interface ChapterCache {
  [chapterId: string]: CachedChapterContent;
}

/**
 * Gets the localStorage key for chapter cache based on project ID
 */
export function getChapterCacheKey(projectId: string): string {
  return `storylikho_cache_chapters_${projectId}`;
}

/**
 * Retrieves chapter content from localStorage cache first
 * Returns null if not found in cache
 */
export function getCachedChapterContent(projectId: string, chapterId: string): CachedChapterContent | null {
  try {
    const cacheKey = getChapterCacheKey(projectId);
    const cachedData = localStorage.getItem(cacheKey);
    
    if (!cachedData) {
      return null;
    }
    
    const chapterCache: ChapterCache = JSON.parse(cachedData);
    return chapterCache[chapterId] || null;
  } catch (error) {
    console.error('Error reading from chapter cache:', error);
    return null;
  }
}

/**
 * Stores chapter content in localStorage cache
 */
export function setCachedChapterContent(
  projectId: string, 
  chapterId: string, 
  content: string, 
  versionHistory: CachedChapterContent['versionHistory'] = [],
  status?: string
): void {
  try {
    const cacheKey = getChapterCacheKey(projectId);
    
    // Get existing cache or create new one
    let chapterCache: ChapterCache = {};
    const existing = localStorage.getItem(cacheKey);
    if (existing) {
      chapterCache = JSON.parse(existing);
    }
    
    // Update or create chapter entry
    chapterCache[chapterId] = {
      content,
      versionHistory,
      lastModified: Date.now(),
      status
    };
    
    // Save back to localStorage
    localStorage.setItem(cacheKey, JSON.stringify(chapterCache));
  } catch (error) {
    console.error('Error writing to chapter cache:', error);
  }
}

/**
 * Updates version history for a cached chapter
 */
export function updateCachedChapterVersionHistory(
  projectId: string,
  chapterId: string,
  newVersion: {
    content: string;
    generatedSummary: string;
    timestamp: number;
  }
): void {
  try {
    const cached = getCachedChapterContent(projectId, chapterId);
    if (cached) {
      const updatedHistory = [...cached.versionHistory, newVersion];
      setCachedChapterContent(
        projectId,
        chapterId,
        newVersion.content,
        updatedHistory,
        cached.status
      );
    }
  } catch (error) {
    console.error('Error updating cached chapter version history:', error);
  }
}

/**
 * Removes a chapter from cache
 */
export function removeCachedChapter(projectId: string, chapterId: string): void {
  try {
    const cacheKey = getChapterCacheKey(projectId);
    const cachedData = localStorage.getItem(cacheKey);
    
    if (cachedData) {
      const chapterCache: ChapterCache = JSON.parse(cachedData);
      delete chapterCache[chapterId];
      localStorage.setItem(cacheKey, JSON.stringify(chapterCache));
    }
  } catch (error) {
    console.error('Error removing cached chapter:', error);
  }
}

/**
 * Clears all cached chapters for a project
 */
export function clearChapterCache(projectId: string): void {
  try {
    const cacheKey = getChapterCacheKey(projectId);
    localStorage.removeItem(cacheKey);
  } catch (error) {
    console.error('Error clearing chapter cache:', error);
  }
}